import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, PenLine, Search, X, Eye } from "lucide-react";
import Seo from "../components/Seo";
import { BlogSubscribeBox } from "../components/BlogSubscribeBox";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : "";

const CardMeta = ({ p }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
    <span>{fmtDate(p.published_at)}</span>
    <span className="inline-flex items-center gap-1">
      <Clock className="w-3 h-3" /> {p.reading_min} min read
    </span>
    {(p.tags || []).slice(0, 3).map((t) => (
      <span key={t} className="rounded-full border border-white/10 px-2 py-0.5 text-[#39FF14]">{t}</span>
    ))}
  </div>
);

const CardReads = ({ p }) => (
  <div
    className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600"
    data-testid="blog-card-reads"
  >
    <Eye className="w-3 h-3" /> {(p.views || 0).toLocaleString()} read{(p.views || 0) === 1 ? "" : "s"}
  </div>
);

export const filterPosts = (rows, q, tag) => {
  const n = (s) => (s || "").toLowerCase();
  const query = n(q).trim();
  return rows.filter(
    (p) =>
      (!tag || (p.tags || []).includes(tag)) &&
      (!query ||
        n(p.title).includes(query) ||
        n(p.excerpt).includes(query) ||
        (p.tags || []).some((t) => n(t).includes(query)))
  );
};

export default function BlogPage() {
  const [data, setData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/blog`)
      .then((r) => (r.ok ? r.json() : { rows: [] }))
      .then(setData)
      .catch(() => setData({ rows: [] }));
    const t = window.localStorage.getItem(AUTH_KEY);
    fetch(`${API}/api/admin/me`, {
      credentials: "include",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => setIsAdmin(r.ok))
      .catch(() => {});
  }, []);

  const rows = data?.rows || [];
  const allTags = [...new Set(rows.flatMap((p) => p.tags || []))].slice(0, 10);
  const searching = q.trim() !== "" || tag !== null;
  const filtered = filterPosts(rows, q, tag);
  const latest = rows.slice(0, 3);
  const earlier = rows.slice(3);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-page">
      <Seo
        title="EVIQ blog — daily EV battery guides for every brand"
        description="Daily owner-focused articles on EV battery care, charging habits, warranty claims, and buying or selling used EVs — covering Tesla, Ford, Hyundai/Kia, GM, Volvo, Rivian and more."
        canonical="https://eviq.pro/blog"
      />
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="blog-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to eviq.pro
          </Link>
          {isAdmin && (
            <Link
              to="/admin/blog"
              data-testid="blog-post-new-btn"
              className="rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1.5"
              style={{ borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }}
            >
              <PenLine className="w-3 h-3" /> Post new article
            </Link>
          )}
        </div>

        <div className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">The EVIQ blog</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold leading-tight">
            Daily guides for <span className="brand-text-gradient">every EV owner.</span>
          </h1>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
            Battery care, charging habits, warranty claims, and buying or selling used EVs —
            for every brand, not just one. Plain language, real lithium-ion science.
          </p>
        </div>

        {rows.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="blog-search-bar">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles by keyword or tag…"
                data-testid="blog-search-input"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] pl-10 pr-10 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14]/50 transition-colors"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                  data-testid="blog-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="blog-tag-filters">
                {allTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTag(tag === t ? null : t)}
                    data-testid={`blog-tag-${t}`}
                    className="rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors"
                    style={
                      tag === t
                        ? { borderColor: "rgba(57,255,20,0.6)", color: "#39FF14", background: "rgba(57,255,20,0.1)" }
                        : { borderColor: "rgba(255,255,255,0.12)", color: "#a1a1aa" }
                    }
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-8 space-y-4" data-testid="blog-list">
          {data === null ? (
            <div className="text-xs text-zinc-500 font-mono">Loading articles…</div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="blog-empty-state">
              <div className="text-white font-medium text-sm">First articles arriving shortly.</div>
              <p className="mt-1 text-xs text-zinc-500">
                New owner guides publish here daily. Check back tomorrow.
              </p>
            </div>
          ) : searching ? (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500" data-testid="blog-results-label">
                {filtered.length} result{filtered.length === 1 ? "" : "s"}
              </div>
              {filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="blog-no-results">
                  <div className="text-white font-medium text-sm">No articles match.</div>
                  <p className="mt-1 text-xs text-zinc-500">Try a different keyword or clear the tag filter.</p>
                </div>
              ) : (
                filtered.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="block rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-[#39FF14]/40 transition-colors"
                    data-testid="blog-card"
                  >
                    <CardMeta p={p} />
                    <h2 className="mt-2 text-white font-semibold text-base leading-snug">{p.title}</h2>
                    {p.excerpt && <p className="mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{p.excerpt}</p>}
                    <CardReads p={p} />
                  </Link>
                ))
              )}
            </>
          ) : (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500" data-testid="blog-latest-label">
                Latest
              </div>
              <div className="grid gap-4 md:grid-cols-3" data-testid="blog-latest-grid">
                {latest.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="block rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-[#39FF14]/40 transition-colors"
                    data-testid="blog-card"
                  >
                    <CardMeta p={p} />
                    <h2 className="mt-2 text-white font-semibold text-[15px] leading-snug">{p.title}</h2>
                    {p.excerpt && <p className="mt-1.5 text-[12px] text-zinc-500 leading-relaxed line-clamp-3">{p.excerpt}</p>}
                    <CardReads p={p} />
                  </Link>
                ))}
              </div>
              {earlier.length > 0 && (
                <>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 pt-4" data-testid="blog-earlier-label">
                    Earlier articles
                  </div>
                  {earlier.map((p) => (
                    <Link
                      key={p.id}
                      to={`/blog/${p.slug}`}
                      className="block rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/25 transition-colors"
                      data-testid="blog-card"
                    >
                      <CardMeta p={p} />
                      <h2 className="mt-2 text-white font-semibold text-base leading-snug">{p.title}</h2>
                      {p.excerpt && <p className="mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{p.excerpt}</p>}
                    </Link>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className="mt-10">
          <BlogSubscribeBox />
        </div>
      </div>
    </div>
  );
}
